import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { ToolCard } from "@/components/ToolCard";
import { Crown, Layers, Sparkles, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);
  const [stack, setStack] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [stackLimit, setStackLimit] = useState(12);
  const [pairingsLoading, setPairingsLoading] = useState(false);

  const loadStack = useCallback(async () => {
    const r = await api.get("/stack");
    setStack(r.data.stack || []);
    setPairings(r.data.pairings || []);
    setStackLimit(r.data.limit || 12);
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/favorites").then(r => setFavs(r.data.favorites));
    loadStack();
  }, [user, loadStack]);

  const suggestPairings = async () => {
    setPairingsLoading(true);
    try {
      const r = await api.post("/stack/pairings");
      setPairings(r.data.pairings || []);
    } finally {
      setPairingsLoading(false);
    }
  };

  const onPinToggle = () => {
    // Reload stack after pin/unpin
    loadStack();
    setPairings([]); // pairings are invalidated server-side too
  };

  if (!user) {
    return <Layout hideRightPanel>
      <div className="max-w-[520px] py-16">
        <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#5B6478] mb-2">Dashboard</div>
        <h1 className="font-display font-semibold text-[#0B1220] tracking-tight leading-[1.15] mb-2.5" style={{ fontSize: "clamp(1.5rem, 2.2vw, 1.75rem)", letterSpacing: "-0.02em" }}>Sign in to view your dashboard</h1>
        <p className="text-[13px] text-[#5B6478] mb-6 leading-relaxed">Save your stack, favorites, and analytics all in one place.</p>
        <Link to="/login" className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-[#F97316] hover:bg-[#EA6A0A] text-white text-[13px] font-semibold transition-colors" data-testid="dashboard-signin-cta">Sign in</Link>
      </div>
    </Layout>;
  }

  const tier = user.is_pro ? "Wikifi All Access" : user.is_plus ? "Wikifi Creator" : "Free";
  const stackSlugs = new Set(stack.map(t => t.slug));
  const toolBySlug = Object.fromEntries(stack.map(t => [t.slug, t]));

  return (
    <Layout hideRightPanel>
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#5B6478] font-semibold mb-1.5">Welcome back</div>
          <h1 className="font-display font-semibold text-[#0B1220] tracking-tight leading-[1.15]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 1.75rem)", letterSpacing: "-0.02em" }}>{user.name}</h1>
          <div className="text-[13px] text-[#5B6478] mt-1">{user.email}</div>
        </div>
        <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#0B1220] border border-[#E7EAF0] rounded-full px-3 py-1.5" data-testid="dashboard-tier-chip">
          <Crown size={12} className="text-[#F97316]"/> {tier}
        </div>
      </header>

      {/* MY STACK */}
      <section className="mb-10" data-testid="my-stack-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#5B6478] font-semibold mb-1.5">
              <Layers size={11} /> My Stack
            </div>
            <h2 className="font-display text-[18px] font-semibold text-[#0B1220] tracking-tight leading-tight">Your AI toolkit</h2>
            <div className="text-[13px] text-[#5B6478] mt-1">
              {stack.length}/{stackLimit} pinned · pin any tool from the directory to build your daily-driver kit.
            </div>
          </div>
          {stack.length >= 2 && (
            <button onClick={suggestPairings} disabled={pairingsLoading}
                    data-testid="stack-pairings-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#F97316] hover:bg-[#EA6A0A] text-white text-[12.5px] font-semibold transition-colors disabled:opacity-60">
              {pairingsLoading ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
              Suggest pairings
            </button>
          )}
        </div>

        {stack.length === 0 ? (
          <div className="rounded-lg bg-white border border-dashed border-[#E7EAF0] p-8 text-center">
            <Layers size={24} className="mx-auto text-[#8A93A6] mb-2"/>
            <p className="text-[13px] text-[#5B6478] mb-3">Your stack is empty. Pin your favorite tools to see them here.</p>
            <Link to="/" className="inline-flex items-center h-9 px-4 rounded-md bg-[#F97316] hover:bg-[#EA6A0A] text-white text-[12.5px] font-semibold transition-colors">Browse the directory</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stack.map(t => (
              <ToolCard key={t.slug} tool={t} isPinned onPinToggle={onPinToggle}
                        isFav={(user.favorites || []).includes(t.slug)} />
            ))}
          </div>
        )}

        {pairings.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stack-pairings-list">
            {pairings.map((p, idx) => (
              <div key={idx} className="rounded-lg bg-white border border-[#E7EAF0] p-5">
                <div className="text-[9.5px] font-semibold tracking-[0.14em] text-[#F97316] uppercase mb-1">Pairing</div>
                <div className="font-display text-[15px] font-semibold mb-1 leading-tight text-[#0B1220]">{p.title}</div>
                <p className="text-[12.5px] text-[#5B6478] mb-3 leading-relaxed">{p.why}</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.slugs.map(s => {
                    const inStack = stackSlugs.has(s);
                    const label = (toolBySlug[s]?.name) || s;
                    return (
                      <Link key={s} to={`/tool/${s}`}
                            className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${inStack
                              ? "border-[#0B1220] bg-[#0B1220] text-white"
                              : "border-[#E7EAF0] text-[#0B1220] hover:border-[#0B1220]"}`}>
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FAVORITES */}
      <section className="mb-10">
        <h2 className="font-display text-[18px] font-semibold text-[#0B1220] tracking-tight leading-tight mb-4">Your favorites</h2>
        {favs.length === 0 ? (
          <p className="text-[13px] text-[#5B6478]">No favorites yet. Heart a tool from the directory.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {favs.map(t => <ToolCard key={t.slug} tool={t} isFav isPinned={stackSlugs.has(t.slug)} onPinToggle={onPinToggle}/>)}
          </div>
        )}
      </section>

      {!user.is_plus && !user.is_pro && (
        <section className="rounded-lg bg-white border border-[#E7EAF0] p-7">
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#F97316] mb-2">Wikifi Creator</div>
          <h2 className="font-display text-[18px] font-semibold text-[#0B1220] mb-1.5 tracking-tight">Unlock the full AI Bible</h2>
          <p className="text-[13px] text-[#5B6478] mb-5 leading-relaxed max-w-xl">Ad-free browsing, the full prompt library, Humanize, and unlimited THE ALGORITHM.</p>
          <Link to="/pricing" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#0B1220] text-white text-[12.5px] font-semibold hover:brightness-110 transition-all">Upgrade to Wikifi Creator</Link>
        </section>
      )}
    </Layout>
  );
}
